import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { connect } from 'react-redux';
import cn from 'classnames';
import './OpportunityPricingPanel.scss';

import { updateUserSettings } from '../../../../Actions/UserActions';
import { fetchUserOpportunityExpectedFee } from '../../../../APIClient/opportunities';

import { getPrettyNumber, getPrettyDate } from '../../../../Helpers/formatting';
import { getUserId, getOpportunityRequests } from '../../../../Helpers/user_helpers';
import { getOpportunityResultRoi, opportunityExpectsLinks, opportunityExpectsMentions } from '../../../../Helpers/opportunity_helpers';
import {
  OPPORTUNITY_LOW_END_EXPECTED_FEE_ADJUSTMENT,
  OPPORTUNITY_HIGH_END_EXPECTED_FEE_ADJUSTMENT,
  OPPORTUNITY_LOW_END_EXPECTED_EARNINGS_ADJUSTMENT,
  OPPORTUNITY_HIGH_END_EXPECTED_EARNINGS_ADJUSTMENT
} from '../../../../Helpers/opportunity_constants';

import Tooltip from '../../../General/Tooltip';
import { scaleLog } from 'd3';

let debouncer;
const OpportunityPricingPanel = props => {
  const { user } = props;
  let { opportunitiesROIPerLinkLast90, monthlyVolume, opportunityCompletionRateLast90 } = user?.profile?.stats || {};
  const applyMeterBounds = value => (value > 98.5 ? 98.5 : value < 1.5 ? 1.5 : value);
  const averageRoiLeftMargin = applyMeterBounds(
    opportunitiesROIPerLinkLast90 ? (opportunitiesROIPerLinkLast90 > 10 ? 100 : opportunitiesROIPerLinkLast90 * 10) : 0
  );
  const averageVolumeLeftMargin = applyMeterBounds(
    monthlyVolume
      ? scaleLog()
          .base(10)
          .domain([1, 1e6])
          .range([1.5, 98.5])(monthlyVolume)
      : 0
  );

  const [expectationCount, setExpectationCount] = useState(3);
  const [expectedUpFrontFee, setExpectedUpFrontFee] = useState(null);

  const [isLoading, setIsLoading] = useState(true);
  const [showRecentResults, setShowRecentResults] = useState(false);
  const recentOpportunityResults = getOpportunityRequests(user)
    .filter(opportunity => opportunity.result?.isCompleted)
    .sort((a, b) => b.id - a.id)
    .slice(0, 5);

  useEffect(() => {
    setIsLoading(true);

    const fetchExpectedFee = () => {
      fetchUserOpportunityExpectedFee(getUserId(user), expectationCount)
        .then(data => {
          setExpectedUpFrontFee(data?.recommended_cost);
        })
        .catch(() => window.ALERT.error('Unable to fetch opportunity pricing'))
        .finally(() => setIsLoading(false));
    };

    clearTimeout(debouncer);
    if (expectationCount > 0) debouncer = setTimeout(() => fetchExpectedFee(), 500);
    else setIsLoading(false);
  }, [expectationCount]);

  const [lowEndExpectedFee, highEndExpectedFee] = expectedUpFrontFee
    ? [expectedUpFrontFee * OPPORTUNITY_LOW_END_EXPECTED_FEE_ADJUSTMENT, expectedUpFrontFee * OPPORTUNITY_HIGH_END_EXPECTED_FEE_ADJUSTMENT]
    : [0, 0];
  const [lowEndExpectedEarnings, highEndExpectedEarnings] = expectedUpFrontFee
    ? [lowEndExpectedFee * OPPORTUNITY_LOW_END_EXPECTED_EARNINGS_ADJUSTMENT, highEndExpectedFee * OPPORTUNITY_HIGH_END_EXPECTED_EARNINGS_ADJUSTMENT]
    : [0, 0];

  return (
    <div className='opportunity-pricing-panel-outer'>
      <div className='opportunity-pricing-panel-inner'>
        <div className='settings-section'>
          <div className='opportunity-pricing-panel-expectation-container'>
            <div className='opportunity-pricing-panel-description-text'>
              Opportunities offer brands an alternative way to spend their advertising budget on you as opposed to standard advertising channels.
            </div>
            <div className='expectations-setting-input-container'>
              <input
                className='settings-input'
                type='number'
                // value={expectationCount}
                value={expectationCount}
                placeholder='0'
                onChange={e => setExpectationCount(e.target.value)}
              />
              <span>Link{expectationCount === '1' ? '' : 's'}</span>
            </div>
          </div>

          <div className='opportunity-pricing-panel-costs-container'>
            <div className='opportunity-pricing-panel-cost' id='up-front-fee'>
              <img src='https://static.shopmy.us/Home/Opportunities/opportunity-up-front-fee.png' alt='Upfront Fee' />
              <div className='settings-section-title'>Your Likely Up-front Fee</div>
              <div className={cn('opportunity-pricing-panel-fees-value', { isLoading })}>
                ${getPrettyNumber(lowEndExpectedFee)} - ${getPrettyNumber(highEndExpectedFee)}
              </div>
              <div className='opportunity-pricing-panel-description-text'>
                Brands offer small up-front fees as guaranteed earnings for promoting their products. These are smaller numbers than paid partnerships
                because there are no briefs and you should be able to do dozens each month.
              </div>
            </div>

            <div className='opportunity-pricing-panel-cost' id='likely-earnings'>
              <img src='https://static.shopmy.us/Home/Opportunities/opportunity-likely-earnings.png' alt='Likely Earnings' />
              <div className='settings-section-title'>Your Likely Earnings per Opportunity</div>
              <div className={cn('opportunity-pricing-panel-fees-value', { isLoading })}>
                ${getPrettyNumber(lowEndExpectedEarnings)} - ${getPrettyNumber(highEndExpectedEarnings)}
              </div>
              <div className='opportunity-pricing-panel-description-text'>
                In addition to the fixed offer, you earn standard affiliate commissions, increasing your earnings to be even more competitive.
              </div>
            </div>
          </div>
        </div>

        <div className='settings-section major-factor-container'>
          <div className='major-factor-section'>
            <div className='opportunity-pricing-panel-header'>Major Factors</div>
            <p className='opportunity-pricing-panel-description-text'>
              Our pricing recommendation systems are dynamic and adjust to your historical performance across opportunities, so your prices rise as
              you perform better. Below you’ll see more explainers.
            </p>
          </div>

          <div className='major-factor-section'>
            <div className='settings-section-title'>Average Opportunity ROI</div>
            <p className='opportunity-pricing-panel-description-text'>
              To ensure they continue to invest in you and not paid advertising, you need to perform better than their advertising alternatives, which
              typically offer 3 - 8x return depending on the type of brand. This is calculated over the last 90 days if you have enough opportunities.
            </p>

            <div className='opportunity-pricing-panel-chart-container'>
              <div className='opportunity-pricing-panel-chart-value' style={{ marginLeft: `${averageRoiLeftMargin}%` }}>
                <div className='average-chart-value'>
                  {typeof opportunitiesROIPerLinkLast90 === 'number' ? `${opportunitiesROIPerLinkLast90.toFixed(1)}x` : '$0'}
                </div>
                <div className='average-chart-text'>90 Day Average ROI</div>
              </div>

              <div className='opportunity-pricing-panel-chart-meter-container'>
                <div className='opportunity-pricing-panel-chart-full-meter' />
                <div className='opportunity-pricing-panel-chart-expected-meter' />
                <div className='opportunity-pricing-panel-chart-average-mark' style={{ left: `${averageRoiLeftMargin}%` }} />
              </div>

              <div className='opportunity-pricing-panel-chart-meter-text-container'>
                <div>0x</div>
                <div className='opportunity-pricing-panel-chart-expected-text'>Expected ROI: 3x - 8x</div>
                <div>10x</div>
              </div>
            </div>

            {showRecentResults && (
              <div className='opportunity-pricing-panel-recent-results-container'>
                {recentOpportunityResults.map(opportunityRequest => {
                  const opportunity = opportunityRequest.opportunity;
                  const { trackingEndsAt, trackingStartsAt, eventEndsAt, eventStartsAt } = opportunity;
                  const result = opportunityRequest.result;
                  const parsedStats = result?.stats ? JSON.parse(result.stats) : {};
                  const fixedFee = opportunityRequest.payment_tier?.fixedFee || 0;
                  const views = parsedStats.views || 0;
                  const roi = getOpportunityResultRoi(opportunity, { stats: result?.stats, fixedFee })?.toFixed(1);
                  const isMentionOpportunity = opportunityExpectsMentions(opportunity) && !opportunityExpectsLinks(opportunity);
                  const volume =
                    opportunityExpectsMentions(opportunity) && opportunityExpectsLinks(opportunity)
                      ? parsedStats.volume + parsedStats.emv || 0
                      : opportunityExpectsMentions(opportunity)
                      ? parsedStats.emv || 0
                      : opportunityExpectsLinks(opportunity)
                      ? parsedStats.volume || 0
                      : parsedStats.volume + parsedStats.emv || 0;

                  const roiExplainer = !fixedFee ? 'Since you were not paid, we calculated the ROI based on the average opportunity cost.' : null;

                  return (
                    <div key={opportunity.id} className='opportunity-pricing-panel-recent-result'>
                      <div>
                        <div className='opportunity-pricing-panel-recent-result-opportunity-title'>{opportunity.title}</div>
                        <div className='opportunity-pricing-panel-recent-result-opportunity-description'>
                          {opportunityRequest.brand.name} • {getPrettyDate(trackingStartsAt || eventStartsAt)} -{' '}
                          {getPrettyDate(trackingEndsAt || eventEndsAt)}
                        </div>
                      </div>

                      <div className='opportunity-pricing-panel-recent-result-stat'>
                        <div className='opportunity-pricing-panel-recent-result-title'>${getPrettyNumber(fixedFee)}</div>
                        <div className='opportunity-pricing-panel-recent-result-description'>Paid</div>
                      </div>

                      <div className='opportunity-pricing-panel-recent-result-stat'>
                        <div className='opportunity-pricing-panel-recent-result-title'>
                          {isMentionOpportunity ? '' : '$'}
                          {getPrettyNumber(isMentionOpportunity ? views : volume)}
                        </div>
                        <div className='opportunity-pricing-panel-recent-result-description'>{isMentionOpportunity ? 'Views' : 'Volume'}</div>
                      </div>

                      <div className='opportunity-pricing-panel-recent-result-stat'>
                        {isMentionOpportunity ? (
                          <div className='opportunity-pricing-panel-recent-result-description'>
                            Mention Opportunities not used in overall calculation
                          </div>
                        ) : roiExplainer ? (
                          <Tooltip message={roiExplainer}>
                            <div className='opportunity-pricing-panel-recent-result-title'>
                              <b>{isNaN(roi) ? 0 : roi}x</b>
                            </div>
                            <div className='opportunity-pricing-panel-recent-result-description'>
                              ROI
                              <FontAwesomeIcon icon={faInfoCircle} />
                            </div>
                          </Tooltip>
                        ) : (
                          <>
                            <div className='opportunity-pricing-panel-recent-result-title'>
                              <b>{isNaN(roi) ? 0 : roi}x</b>
                            </div>
                            <div className='opportunity-pricing-panel-recent-result-description'>ROI</div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className='opportunity-pricing-panel-recent-results' onClick={() => setShowRecentResults(!showRecentResults)}>
              {showRecentResults ? 'Hide Recent Results' : 'Show Recent Results'}
              <FontAwesomeIcon icon={showRecentResults ? faChevronUp : faChevronDown} />
            </div>
          </div>

          <div className='major-factor-section'>
            <div className='settings-section-title'>Monthly Order Volume</div>
            <p className='opportunity-pricing-panel-description-text'>
              Another primary factor that we consider is your average monthly order volume (AMV) over the last 6 months.
            </p>

            <div className='opportunity-pricing-panel-chart-container'>
              <div className='opportunity-pricing-panel-chart-value' style={{ marginLeft: `${averageVolumeLeftMargin}%` }}>
                <div className='average-chart-value'>{monthlyVolume ? `$${getPrettyNumber(monthlyVolume)}` : '$0'}</div>
                <div className='average-chart-text'>AMV</div>
              </div>

              <div className='opportunity-pricing-panel-chart-meter-container'>
                <div className='opportunity-pricing-panel-chart-full-meter' />
                <div className='opportunity-pricing-panel-chart-average-mark' style={{ left: `${averageVolumeLeftMargin}%` }} />
              </div>

              <div className='opportunity-pricing-panel-chart-meter-text-container'>
                <div>$0</div>
                <div>$10</div>
                <div>$100</div>
                <div>$1k</div>
                <div>$10k</div>
                <div>$100k</div>
                <div>$1m</div>
              </div>
            </div>
          </div>

          <div className='major-factor-section'>
            <div className='settings-section-title'>Additional Considerations</div>
            <p className='opportunity-pricing-panel-description-text'>
              We also take into social factors such as following & enagement, your recent opportunity completion rate (
              {Math.round(opportunityCompletionRateLast90 * 100)}%), and other ShopMy factors such as responsiveness to brands and general platform
              activity.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

OpportunityPricingPanel.propTypes = {
  user: PropTypes.object.isRequired,
  updateUserSettings: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  const { user, ui } = state;
  return { user, ui };
};

export default connect(mapStateToProps, {
  updateUserSettings
})(OpportunityPricingPanel);
